// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/hooks/useDav.ts
// ════════════════════════════════════════════════════════════════════════════

import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { davService, type ListaFiltrosPessoa } from '../services/davService';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import type { CriarDavRequest, AtualizarDavRequest } from '../types';

interface UseDavParams {
  pagina?: number;
  tamanhoPagina?: number;
  /** Busca, status, período, cliente, vendedor e ordem — aplicados no servidor. */
  filtros?: ListaFiltrosPessoa;
}

export const useDav = ({ pagina = 1, tamanhoPagina = 20, filtros = {} }: UseDavParams = {}) => {
  const queryClient = useQueryClient();

  // A lista já traz `resumo` (KPIs do filtro sem o status): uma ida ao servidor.
  const { data: paginado, isLoading, isFetching } = useQuery({
    queryKey: ['dav', pagina, tamanhoPagina, filtros],
    queryFn: () => davService.getAll(pagina, tamanhoPagina, filtros),
    placeholderData: keepPreviousData, // a tabela não pisca a cada letra da busca
  });

  const davs          = paginado?.itens       ?? [];
  const total         = paginado?.total        ?? 0;
  const totalPaginas  = paginado?.totalPaginas ?? 1;
  const resumo        = paginado?.resumo;

  const createMutation = useMutation({
    mutationFn: (request: CriarDavRequest) => davService.create(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dav'] });
      toast.success('Pedido de venda criado com sucesso');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AtualizarDavRequest }) =>
      davService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dav'] });
      toast.success('Pedido de venda atualizado com sucesso');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => davService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dav'] });
      toast.success('Pedido de venda excluído com sucesso');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const copiarMutation = useMutation({
    mutationFn: (id: number) => davService.copiar(id),
    onSuccess: (dav) => {
      queryClient.invalidateQueries({ queryKey: ['dav'] });
      toast.success(`DAV copiado com sucesso — novo nº ${dav.numeroDav}`);
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  return {
    davs,
    total,
    totalPaginas,
    resumo,
    isLoading,
    isFetching,
    createDav: createMutation.mutate,
    updateDav: updateMutation.mutate,
    deleteDav: deleteMutation.mutate,
    copiarDav: copiarMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isCopying: copiarMutation.isPending,
  };
};
